import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import TrainingTaskModal from "../../pages/Training/TrainingTaskModal";
import Pagination from "../../components/Pagination";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/scormModal.css";
import logo from "../../images/logo.png";

function TrainingPanel() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();

  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const location = useLocation();
  const initialOpenKey = location.state?.openModuleKey || null;
  const initialOpenExternalId = location.state?.openExternalId || null;

  const [trainings, setTrainings] = useState([]);
  const [tab, setTab] = useState("assigned");

  const [showModal, setShowModal] = useState(false);
  const [activeModule, setActiveModule] = useState("");
  const [activeTitle, setActiveTitle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedScore, setSelectedScore] = useState(null);
  const [externalTrainings, setExternalTrainings] = useState([]);
  const [learnerId, setLearnerId] = useState("");
  const [scormUrl, setScormUrl] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const getPaginatedData = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const fetchTrainings = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/trainings/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const internalModules = res.data.internalModules || [];
      const externalModules = res.data.externalModules || [];
      const learner = res.data.learnerId || "";

      const processed = internalModules.map((item) => ({
        ...item,
        status: item.tp_status || "not-started",
        score: item.tp_score ?? null,
        startDate: item.tp_start_date
          ? new Date(item.tp_start_date).toLocaleDateString("en-CA")
          : "",
        finishDate: item.tp_finish_date
          ? new Date(item.tp_finish_date).toLocaleDateString("en-CA")
          : "",
        deadline: item.tm_deadline
          ? new Date(item.tm_deadline).toLocaleDateString("en-CA")
          : "",
      }));

      setTrainings(processed);
      setExternalTrainings(externalModules);
      setLearnerId(learner);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to load trainings", err);
    }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  useEffect(() => {
    if (!initialOpenKey || trainings.length === 0) return;
    const t = trainings.find((x) => x.tm_key === initialOpenKey);
    if (t) {
      handleOpenQuiz(t.tm_key, t.tm_title, t.status, t.score);
      // clear the state so it doesn’t re-open if the user comes back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [initialOpenKey, trainings, navigate, location.pathname]);

  const handleOpenExternalTraining = useCallback(
    async (course) => {
      try {
        // 🔥 1️⃣ Update progress status
        await axios.post(
          `${BASE_URL}/api/trainings/external-progress/start`,
          {
            userId,
            extCourseId: course.id,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        // 🔥 2️⃣ Refresh trainings so UI updates badge immediately
        await fetchTrainings();
      } catch (error) {
        console.error("Failed to update external training status", error);
      }

      // 🔥 3️⃣ Open SCORM
      const backendBase = process.env.REACT_APP_API_BASE_URL.replace(
        "/api",
        "",
      );

      const token = localStorage.getItem("token");

      const url = `${backendBase}/scorm/player.html?course_id=${course.courseId}
        &learner_id=${encodeURIComponent(learnerId)}
        &token=${encodeURIComponent(token)}`;

      setScormUrl(url);
    },
    [BASE_URL, userId, learnerId, fetchTrainings],
  );

  useEffect(() => {
    if (!initialOpenExternalId || externalTrainings.length === 0) return;

    const module = externalTrainings.find(
      (m) => Number(m.id) === Number(initialOpenExternalId),
    );

    if (module) {
      handleOpenExternalTraining(module);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    initialOpenExternalId,
    externalTrainings,
    handleOpenExternalTraining,
    navigate,
    location.pathname,
  ]);

  const formatStatusIcon = (status, score) => {
    if (status === "complete" && score >= 50)
      return <span className="text-success">✔️</span>;
    if (status === "complete" && score < 50)
      return <span className="text-danger">🔁</span>;
    if (status === "in-progress")
      return <span className="text-warning">◐</span>;
    return <span className="text-danger">●</span>;
  };

  const handleOpenQuiz = (moduleKey, title, status, score) => {
    setActiveModule(moduleKey);
    setActiveTitle(title);
    setSelectedStatus(status);
    setSelectedScore(score);
    setShowModal(true);
  };

  const handleReturn = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  const formatExternalGrade = (status, grade) => {
    if (grade) {
      if (grade.toUpperCase() === "PASS") {
        return (
          <span className="badge bg-success text-white fw-bold">Pass</span>
        );
      }

      if (grade.toUpperCase() === "FAIL") {
        return <span className="badge bg-danger text-white fw-bold">Fail</span>;
      }

      return (
        <span className="badge bg-primary text-white fw-bold">{grade}</span>
      );
    }

    if (status && status.toLowerCase().includes("content in progress")) {
      return (
        <span className="badge bg-warning text-dark fw-bold">In Progress</span>
      );
    }

    // ✅ Improved Not Started style
    return (
      <span
        className="badge fw-bold"
        style={{
          backgroundColor: "#e2e3e5",
          color: "#212529",
          border: "1px solid #ced4da",
        }}
      >
        Not Started
      </span>
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Pg. 5 - Training Panel
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleReturn}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Pg. 5 - Training Panel</h2>
          </div>
          <CompanyAddress />
        </div>

        <div className="d-flex justify-content-end mb-3">
          <button
            className={`btn btn-outline-primary me-2 ${
              tab === "assigned" ? "active" : ""
            }`}
            onClick={() => setTab("assigned")}
          >
            Assigned
          </button>

          <button
            className={`btn btn-outline-success me-2 ${
              tab === "external" ? "active" : ""
            }`}
            onClick={() => setTab("external")}
          >
            Assigned (External)
          </button>

          <button
            className={`btn btn-outline-secondary ${
              tab === "review" ? "active" : ""
            }`}
            onClick={() => setTab("review")}
          >
            Review
          </button>
        </div>

        {tab === "assigned" && (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Status</th>
                  <th>Module Name</th>
                  <th>Score</th>
                  {/* <th>Deadline</th> */}
                  <th>Start Date</th>
                  <th>Finish Date</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedData(trainings, currentPage).map((t) => (
                  <tr key={t.tm_id}>
                    <td>{formatStatusIcon(t.status, t.score)}</td>
                    <td>
                      <button
                        className="btn btn-link"
                        onClick={() =>
                          handleOpenQuiz(
                            t.tm_key,
                            t.tm_title,
                            t.status,
                            t.score,
                          )
                        }
                      >
                        {t.tm_title}
                      </button>
                    </td>
                    <td> {t.score || "-"}</td>
                    {/* <td>{t.deadline || "-"}</td> */}
                    <td>{t.startDate || "-"}</td>
                    <td>{t.finishDate || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={currentPage}
              totalItems={trainings.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {tab === "external" && (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Course Name</th>
                  <th>Grade</th>
                  <th>Score</th>
                  <th>Last Update</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {externalTrainings.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted">
                      No external trainings assigned.
                    </td>
                  </tr>
                ) : (
                  externalTrainings.map((course) => (
                    <tr key={course.id}>
                      <td>
                        <button
                          className="btn btn-link"
                          onClick={() => handleOpenExternalTraining(course)}
                        >
                          {course.name}
                        </button>
                      </td>

                      <td>
                        {formatExternalGrade(course.status, course.grade)}
                      </td>

                      <td>{course.score ? course.score : "-"}</td>

                      <td>
                        {course.updated_at
                          ? new Date(course.updated_at).toLocaleDateString(
                              "en-CA",
                            )
                          : "-"}
                      </td>

                      <td>
                        {course.duration_minutes
                          ? `${course.duration_minutes} mins`
                          : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "review" && (
          <div className="alert alert-info text-center">
            🔍 Review tab will display completed trainings later.
          </div>
        )}

        <TrainingTaskModal
          show={showModal}
          onHide={() => setShowModal(false)}
          moduleKey={activeModule}
          title={activeTitle}
          userId={userId}
          tpStatus={selectedStatus}
          tpScore={selectedScore}
          onSubmitted={() => {
            fetchTrainings();
            setShowModal(false);
          }}
        />
      </div>
      {/* 🔥 SCORM Overlay START */}
      {scormUrl && (
        <div className="scorm-overlay">
          <div className="scorm-modal">
            <div className="scorm-modal-header">
              <h5 style={{ margin: 0 }}>Training Module</h5>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setScormUrl(null)}
              >
                Close
              </button>
            </div>

            <div className="scorm-modal-body">
              <iframe
                src={scormUrl}
                title="SCORM Player"
                className="scorm-iframe"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TrainingPanel;
